import { IconCopy } from '@developerz/ui';
import { createSignal, type JSX, onCleanup } from 'solid-js';
import { copyText } from '../lib/clipboard';
import { Button } from './ui/Button';

// The funnel's COPY control — the marketing `CopyLine` behaviour, on the
// dashboard's clipboard seam (`lib/clipboard.ts`, which answers whether the write
// LANDED rather than letting a refused one read as "Copied"):
//
//   - a landed write says so for COPY_RESET_MS, then offers the copy again;
//   - a refused one (non-secure origin, denied permission) is not a dead end
//     when there is on-screen text to fall back to: `onRefused` selects it so
//     the reader's own Cmd-C / Ctrl-C works, and the label says to do that;
//   - the settled word is ALSO written to a polite live region, because a
//     button's own label change is not announced while focus stays on it.
//
// `text` is read at click time, never snapshotted: the enrollment token beside
// it is replaced in place by the next mint.

/** How long a settled state shows before the button offers the copy again. */
export const COPY_RESET_MS = 2000;

type CopyState = 'idle' | 'copied' | 'refused';

export type OnboardingCopyButtonProps = {
  /** What lands on the clipboard. Read at click time. */
  readonly text: () => string;
  /** The resting label, and the button's name — say WHAT it copies when a panel holds two. */
  readonly label: string;
  readonly copiedLabel: string;
  /** What to say when the clipboard refused, e.g. "select and copy". */
  readonly refusedLabel: string;
  /** Select the on-screen text so a manual copy works; absent when nothing is on screen. */
  readonly onRefused?: () => void;
};

export function OnboardingCopyButton(props: OnboardingCopyButtonProps): JSX.Element {
  const [state, setState] = createSignal<CopyState>('idle');
  let timer: ReturnType<typeof setTimeout> | undefined;
  onCleanup(() => {
    if (timer !== undefined) clearTimeout(timer);
  });

  const settle = (next: CopyState): void => {
    setState(next);
    if (timer !== undefined) clearTimeout(timer);
    timer = setTimeout(() => setState('idle'), COPY_RESET_MS);
  };

  const copy = async (): Promise<void> => {
    if (await copyText(props.text())) {
      settle('copied');
      return;
    }
    props.onRefused?.();
    settle('refused');
  };

  const label = (): string =>
    state() === 'copied'
      ? props.copiedLabel
      : state() === 'refused'
        ? props.refusedLabel
        : props.label;

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => void copy()}>
        <IconCopy />
        {label()}
      </Button>
      <span class="sr-only" role="status">
        {state() === 'idle' ? '' : label()}
      </span>
    </>
  );
}
